/**
 * Proveedores.
 *
 * `POST /admin/suppliers` existía desde la migración 0009 y **nunca lo llamó
 * nadie**: había tabla, endpoint y hasta un `<select>` en Inventario, pero
 * ninguna pantalla para crear un proveedor — así que el selector estaba vacío
 * por construcción y toda compra se registraba sin proveedor.
 *
 * Entrada propia en la barra lateral y no una pestaña dentro de Catálogo
 * (decisión del dueño, P6.6), con el mismo layout que las demás secciones de
 * ese nivel: `PageHead`, tabla en una `Card` a ras, y un modal para editar.
 *
 * ── Por qué no hay botón de eliminar ────────────────────────────────────────
 *
 * `purchases.supplier_id` es `on delete restrict`: un proveedor con compras no
 * se puede borrar aunque se apriete el botón. Y ofrecer el borrado solo para
 * los que todavía no tienen ninguna daría dos comportamientos distintos para
 * la misma acción, según un historial que el admin no ve. Se desactiva: sale
 * del selector, las compras viejas conservan su nombre, y se puede volver a
 * prender.
 */

import { useEffect, useRef, useState } from 'react';
import { cedulaLabel, formatMoney, parseMoneyInput, today } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Note, PageHead, Table,
} from '@/components/ui';
import { useSuppliers, type Supplier } from '@/components/common/SupplierPicker';
import { Icon } from '@/components/common/NavIcons';

export function SuppliersPage() {
  const query = useSuppliers();
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [creating, setCreating] = useState(false);
  const [fromInvoice, setFromInvoice] = useState(false);
  const [prefill, setPrefill] = useState<SupplierPrefill | null>(null);
  // Para el aviso «Datos leídos del…»: del XML salen todos los campos: del
  // PDF, solo lo que el documento trae etiquetado (dueño, 3-sep-2026 — el
  // aviso decía «XML» aunque se hubiera subido un PDF).
  const [fuente, setFuente] = useState<'xml' | 'pdf'>('xml');
  /* La factura leída completa (no solo las líneas): registrar la compra
     necesita también la fecha y el número de documento, y guardarla acá
     entera evita una segunda subida del mismo archivo para el paso dos. */
  const [facturaLeida, setFacturaLeida] = useState<ReadInvoice['invoice'] | null>(null);
  const [registrando, setRegistrando] = useState<{ supplierId: string; supplierName: string } | null>(null);

  return (
    <>
      <PageHead
        title="Proveedores"
        subtitle="A quién se le compra. Una compra no se puede registrar sin proveedor."
        actions={
          <>
            <Button onClick={() => setFromInvoice(true)}>Desde una factura</Button>
            <Button variant="primary" onClick={() => setCreating(true)}>Nuevo proveedor</Button>
          </>
        }
      />

      <Async query={query} empty={(d) => d.suppliers.length === 0 && (
        <Empty
          icon="proveedores"
          title="Todavía no hay proveedores"
          hint="Cargá a quién le comprás los insumos: sin proveedor no se puede registrar una compra."
          action={
            <>
              <Button onClick={() => setFromInvoice(true)}>Desde una factura</Button>
              <Button variant="primary" onClick={() => setCreating(true)}>Nuevo proveedor</Button>
            </>
          }
        />
      )}>
        {(data) => (
          <Card flush>
            <Table head={
              <tr>
                <th>Proveedor</th><th>Cédula</th><th>Contacto</th>
                <th className="right">Compras</th><th className="right">Productos</th>
                <th>Estado</th><th />
              </tr>
            }>
              {data.suppliers.map((s) => (
                <tr key={s.id}>
                  <td className="strong"><Cell sub={s.address}>{s.name}</Cell></td>
                  <td className="mono small cedula">{cedulaLabel(s.identification) ?? '—'}</td>
                  <td className="small">
                    <Cell sub={s.email}>
                      {[s.phone, s.whatsapp && `WhatsApp ${s.whatsapp}`].filter(Boolean).join(' · ')
                        || (s.email ? '' : '—')}
                    </Cell>
                  </td>
                  <td className="right">{s.purchase_count}</td>
                  <td className="right">{s.item_count}</td>
                  <td>
                    {s.is_active
                      ? <Badge tone="ok">Activo</Badge>
                      : <Badge tone="neutral">Inactivo</Badge>}
                  </td>
                  <td className="right">
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <Button onClick={() => setEditing(s)}>Editar</Button>
                      <ToggleActive supplier={s} />
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </Async>

      {fromInvoice && (
        <SupplierFromInvoiceModal
          onClose={() => setFromInvoice(false)}
          onPrefill={(p, factura, source) => {
            setFromInvoice(false); setPrefill(p); setFacturaLeida(factura); setFuente(source); setCreating(true);
          }}
          onOpenExisting={(id) => {
            setFromInvoice(false);
            const found = query.data?.suppliers.find((x) => x.id === id);
            if (found) setEditing(found);
          }}
          onRegisterExisting={(supplier, factura, source) => {
            setFromInvoice(false); setFuente(source); setFacturaLeida(factura);
            setRegistrando({ supplierId: supplier.id, supplierName: supplier.name });
          }}
        />
      )}

      {(creating || editing) && (
        <SupplierFormModal
          supplier={editing}
          prefill={prefill ?? undefined}
          intro={prefill ? (
            <Note>
              Datos leídos del {fuente === 'xml' ? 'XML' : 'PDF'} de la factura. Revisalos antes de guardar.
            </Note>
          ) : undefined}
          onClose={() => { setCreating(false); setEditing(null); setPrefill(null); }}
          onSaved={(r) => {
            if (facturaLeida && facturaLeida.lines.length > 0) {
              setRegistrando({ supplierId: r.id, supplierName: r.name });
            }
          }}
        />
      )}

      {registrando && facturaLeida && (
        <RegisterPurchaseModal
          supplierId={registrando.supplierId}
          supplierName={registrando.supplierName}
          invoice={facturaLeida}
          source={fuente}
          onClose={() => { setRegistrando(null); setFacturaLeida(null); }}
        />
      )}
    </>
  );
}

/** Prender o apagar. Es lo que reemplaza al borrado — ver la nota de arriba. */
function ToggleActive({ supplier }: { supplier: Supplier }) {
  const mutation = useApiMutation(
    () => api.patch(`/admin/suppliers/${supplier.id}`, { is_active: supplier.is_active !== 1 }),
    ['/admin/suppliers'],
  );

  return (
    <Button
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
      title={supplier.is_active
        ? 'Deja de ofrecerse al registrar compras. Las compras viejas no se tocan.'
        : 'Vuelve a aparecer en el selector de proveedor.'}
    >
      {supplier.is_active ? 'Desactivar' : 'Activar'}
    </Button>
  );
}

/**
 * Alta y edición completas.
 *
 * Se exporta porque Inventario la reutiliza: cuando el alta rápida crea un
 * proveedor con solo el nombre, esta es la ficha que se ofrece completar
 * inmediatamente después de guardar el producto o la compra.
 */
export type SupplierPrefill = {
  name?: string | null; identification?: string | null; email?: string | null;
  // El WhatsApp no sale de la factura —Hacienda no lo distingue del teléfono
  // en el comprobante— así que nunca llega prellenado, solo a mano.
  phone?: string | null; address?: string | null; notes?: string | null;
};

/**
 * Dar de alta un proveedor leyendo su factura.
 *
 * Los datos que el alta pide —nombre, cédula, teléfono, correo, dirección— son
 * exactamente el bloque `<Emisor>` de un comprobante electrónico. Transcribirlos
 * a mano es copiar de un papel algo que el archivo ya trae, con el error de
 * tipeo incluido.
 *
 * **Lee y propone; no guarda.** El servidor devuelve los campos, acá se ven, y
 * el alta la confirma una persona en el formulario de siempre. Un archivo no
 * crea filas por su cuenta.
 *
 * Pide el XML y no el PDF: el PDF es una representación, el XML es el
 * documento. Del XML los campos salen etiquetados; del PDF habría que adivinar
 * dónde termina la dirección y empieza el teléfono.
 */
function SupplierFromInvoiceModal({ onClose, onPrefill, onOpenExisting, onRegisterExisting }: {
  onClose: () => void;
  onPrefill: (p: SupplierPrefill, invoice: ReadInvoice['invoice'], source: 'xml' | 'pdf') => void;
  onOpenExisting: (id: string) => void;
  /** El proveedor ya estaba cargado: en vez de descartar lo que se acaba de
   *  leer, se puede ir directo a registrar la compra con esos datos. */
  onRegisterExisting: (
    supplier: { id: string; name: string }, invoice: ReadInvoice['invoice'], source: 'xml' | 'pdf',
  ) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [read, setRead] = useState<ReadInvoice | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [nombreArchivo, setNombreArchivo] = useState<string | null>(null);
  const [tipoAjeno, setTipoAjeno] = useState<string | null>(null);

  const upload = useApiMutation<ReadInvoice, File>(
    (file) => {
      const form = new FormData();
      form.set('file', file);
      return api.post('/admin/suppliers/read-invoice', form);
    },
    [],
    { onSuccess: (data) => setRead(data) },
  );

  /* La única puerta de entrada para el archivo, venga del selector o de un
     arrastre. El tipo se valida acá y no solo en `accept`, porque `accept`
     no aplica a lo que se suelta encima. */
  const recibir = (file: File | undefined) => {
    setRead(null);
    setTipoAjeno(null);
    if (!file) return;
    if (!/\.(xml|pdf)$/i.test(file.name)) {
      setTipoAjeno(file.name);
      return;
    }
    setNombreArchivo(file.name);
    upload.mutate(file);
  };

  return (
    <Modal
      title="Agregar desde una factura"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          {read && !read.existing && (
            <Button variant="primary" onClick={() => onPrefill(read.supplier, read.invoice, read.source)}>
              Continuar con estos datos
            </Button>
          )}
          {read?.existing && (
            <>
              <Button onClick={() => onOpenExisting(read.existing!.id)}>
                Abrir {read.existing.name}
              </Button>
              <Button
                variant="primary"
                onClick={() => onRegisterExisting(read.existing!, read.invoice, read.source)}
              >
                Registrar esta compra
              </Button>
            </>
          )}
        </>
      }
    >
      <Note>
        Subí una factura que el proveedor le emitió a Clean Blue: el <strong>XML</strong> o
        su <strong>PDF</strong>. Del XML salen todos los datos, líneas incluidas; del PDF
        se lee lo que el documento declara —cédula, consecutivo, contacto— y lo que no
        se pueda leer queda para completar a mano.
      </Note>

      {upload.error && <Note tone="danger">{upload.error.message}</Note>}
      {tipoAjeno && (
        <Note tone="danger">
          «{tipoAjeno}» no es una factura: se acepta el XML del comprobante o su PDF.
        </Note>
      )}

      {/* La zona de soltar (pedido del dueño, 28-ago-2026). Es un botón de
          verdad —no un div con onClick— para que el teclado y el lector de
          pantalla la operen igual que el mouse; en el teléfono, donde no
          existe el arrastre, es simplemente un blanco grande para tocar. */}
      <button
        type="button"
        className={`dropzone${arrastrando ? ' is-over' : ''}`}
        disabled={upload.isPending}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          recibir(e.dataTransfer.files?.[0]);
        }}
      >
        <Icon name="documento" size={26} />
        <span className="dropzone__txt">
          {upload.isPending
            ? 'Leyendo el comprobante…'
            : arrastrando
              ? 'Soltala acá'
              : nombreArchivo ?? 'Arrastrá la factura acá, o tocá para buscarla'}
        </span>
        <span className="dropzone__sub">XML o PDF · máx. 5 MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".xml,.pdf,text/xml,application/xml,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          recibir(e.target.files?.[0]);
          // El mismo archivo dos veces seguidas debe volver a disparar el
          // change (p. ej. tras corregir el XML): se limpia el value.
          e.target.value = '';
        }}
      />

      {read && (
        <>
          {read.existing ? (
            <Note tone="warn">
              Ese proveedor ya está cargado como <strong>{read.existing.name}</strong>.
              No se crea de nuevo, pero con esta factura ya se puede registrar la compra.
            </Note>
          ) : (
            <Note tone="ok">Leído. Revisá los datos antes de guardar.</Note>
          )}

          <dl className="dl">
            <dt>Proveedor</dt><dd>{read.supplier.name}</dd>
            {read.supplier.identification && (
              <><dt>Cédula</dt><dd className="mono cedula">{cedulaLabel(read.supplier.identification)}</dd></>
            )}
            {read.supplier.phone && <><dt>Teléfono</dt><dd>{read.supplier.phone}</dd></>}
            {read.supplier.email && <><dt>Correo</dt><dd>{read.supplier.email}</dd></>}
            {read.supplier.address && <><dt>Dirección</dt><dd>{read.supplier.address}</dd></>}
          </dl>

          {/* Para que se vea que se leyó la factura correcta. Nada de esto se
              guarda todavía: lo hace el paso siguiente, sea el alta del
              proveedor o «Registrar esta compra» de acá arriba. */}
          <p className="small muted mt-2">
            Factura {read.invoice.document_number ?? ''} · {read.invoice.issued_on?.slice(0, 10)} ·{' '}
            {read.invoice.total} {read.invoice.moneda}
            {read.invoice.lines.length > 0 && (
              <>: {read.invoice.lines.map((l) => l.detalle).filter(Boolean).join(' · ')}</>
            )}
          </p>
          <p className="small muted">
            {read.existing
              ? 'Registrar la compra afecta el inventario de una vez. Abrir el proveedor es solo para editar sus datos.'
              : 'La compra se ofrece registrar en el siguiente paso, después de guardar el proveedor.'}
          </p>
        </>
      )}
    </Modal>
  );
}

interface ReadInvoice {
  source: 'xml' | 'pdf';
  supplier: SupplierPrefill & { name: string };
  existing: { id: string; name: string } | null;
  invoice: {
    document_number: string | null; issued_on: string | null;
    total: string | null; moneda: string;
    lines: {
      detalle: string | null; cantidad: string | null; unidad: string | null;
      precio_unitario: string | null; total_linea: string | null;
    }[];
  };
}

export function SupplierFormModal({ supplier, onClose, intro, prefill, onSaved }: {
  supplier: Supplier | null;
  onClose: () => void;
  /** Contexto extra cuando se abre desde otra pantalla (ej. recién creado). */
  intro?: React.ReactNode;
  /** Avisa que el alta se guardó, con el id nuevo — para encadenar el paso de
   *  productos cuando el proveedor vino de una factura. */
  onSaved?: (r: { id: string; name: string }) => void;
  /** Campos ya leídos de algún lado —hoy, del XML de una factura— para un alta
   *  que todavía NO existe. Distinto de `supplier`: eso ya está guardado y hace
   *  que el modal edite; esto solo llena el formulario y sigue creando. */
  prefill?: SupplierPrefill;
}) {
  const [form, setForm] = useState({
    name: supplier?.name ?? prefill?.name ?? '',
    identification: supplier?.identification ?? prefill?.identification ?? '',
    email: supplier?.email ?? prefill?.email ?? '',
    phone: supplier?.phone ?? prefill?.phone ?? '',
    whatsapp: supplier?.whatsapp ?? '',
    address: supplier?.address ?? prefill?.address ?? '',
    notes: supplier?.notes ?? prefill?.notes ?? '',
  });

  useEffect(() => {
    if (!supplier) return;
    setForm({
      name: supplier.name,
      identification: supplier.identification ?? '',
      email: supplier.email ?? '',
      phone: supplier.phone ?? '',
      whatsapp: supplier.whatsapp ?? '',
      address: supplier.address ?? '',
      notes: supplier.notes ?? '',
    });
  }, [supplier]);

  // `null` y no cadena vacía en los opcionales: vaciar un campo tiene que
  // borrarlo en la base, no guardar un '' que después se muestra como si
  // hubiera algo cargado.
  const payload = () => ({
    name: form.name.trim(),
    identification: form.identification.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    whatsapp: form.whatsapp.trim() || null,
    address: form.address.trim() || null,
    notes: form.notes.trim() || null,
  });

  const mutation = useApiMutation<{ id: string; name: string }>(
    () => (supplier
      ? api.patch(`/admin/suppliers/${supplier.id}`, payload())
      : api.post('/admin/suppliers', payload())),
    ['/admin/suppliers', '/admin/inventory'],
    { onSuccess: (r) => { onClose(); if (!supplier) onSaved?.(r); } },
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title={supplier ? `Editar ${supplier.name}` : 'Nuevo proveedor'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{intro ? 'Después' : 'Cancelar'}</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      {intro}
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Nombre" required hint="Como lo nombra la factura. No se puede repetir.">
        <input className="input" value={form.name} onChange={set('name')} autoFocus={!intro} />
      </Field>
      <div className="grid grid--2">
        <Field label="Cédula jurídica o física">
          <input className="input mono" value={form.identification} onChange={set('identification')} />
        </Field>
        <Field label="Teléfono">
          <input className="input" type="tel" value={form.phone} onChange={set('phone')} />
        </Field>
      </div>
      <div className="grid grid--2">
        <Field label="Correo">
          <input className="input" type="email" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="WhatsApp" hint="Si es distinto del teléfono de arriba.">
          <input className="input" type="tel" value={form.whatsapp} onChange={set('whatsapp')} />
        </Field>
      </div>
      <Field label="Dirección">
        <input className="input" value={form.address} onChange={set('address')} />
      </Field>
      <Field label="Notas" hint="Días de entrega, contacto de ventas, condiciones de pago.">
        <input className="input" value={form.notes} onChange={set('notes')} />
      </Field>
    </Modal>
  );
}

/** El catálogo, tal como lo necesita esta pantalla: id, nombre y unidad de
 *  compra (para prellenar la de un producto nuevo, igual que hace `Field`
 *  «Se compra por» en `Inventory.tsx`). */
interface CatalogItem { id: string; name: string; purchase_unit: string }

/**
 * Una línea de la factura, ya editable, camino a convertirse en una línea de
 * compra real. `key` es el índice dentro de `invoice.lines` — no el id de
 * nada, porque todavía no hay id de nada: puede terminar en un producto que ni
 * existe.
 */
interface PurchaseLineDraft {
  key: number;
  detalle: string | null;
  unidad: string | null;
  /* Lo que trajo la factura, sin tocar — se muestra como referencia junto a
     los campos editables, para que se vea de dónde salió lo prellenado. */
  cantidadFactura: string | null;
  precioFactura: string | null;
  included: boolean;
  isNew: boolean;
  itemId: string;
  newName: string;
  quantity: string;
  unitPrice: string;
}

/**
 * Igual que `parseMoneyInput` pero sin escalar a céntimos: la cantidad de una
 * línea comparte el mismo problema de separador ambiguo que el precio (ver
 * `comprobante-pdf.ts`, «SubTotal y Precio Unitario no usan el mismo
 * separador»), así que se resuelve con la misma regla — solo que acá el
 * resultado es la cantidad tal cual, no céntimos.
 */
function cantidadDe(raw: string | null): string {
  if (!raw) return '';
  const minor = parseMoneyInput(raw);
  return minor == null ? '' : String(minor / 100);
}

/** El precio de la línea, prellenado en el mismo formato que ya sabe leer
 *  `parseMoneyInput` al guardar — así el número que se ve es el mismo que se
 *  manda, sin una conversión invisible entre medio. */
function precioDe(raw: string | null): string {
  if (!raw) return '';
  const minor = parseMoneyInput(raw);
  return minor == null ? '' : formatMoney(minor, { symbol: false });
}

/**
 * El paso dos, tanto del alta desde factura como de la factura de un
 * proveedor ya cargado: convertir las líneas leídas en una compra real.
 *
 * Reemplaza a lo que antes era «agregar productos al catálogo» — el dueño
 * pidió que la carga de la factura **afecte el inventario**, no solo que
 * precargue el catálogo (3-sep-2026): confirmar acá crea lo que falte del
 * catálogo y de una vez registra la compra, con su movimiento de stock y su
 * gasto — lo que ya hace `POST /purchases` en un solo batch.
 */
function RegisterPurchaseModal({ supplierId, supplierName, invoice, source, onClose }: {
  supplierId: string;
  supplierName: string;
  invoice: ReadInvoice['invoice'];
  source: 'xml' | 'pdf';
  onClose: () => void;
}) {
  const inventario = useApi<{ items: CatalogItem[] }>('/admin/inventory');
  const items = inventario.data?.items ?? [];

  const [purchasedOn, setPurchasedOn] = useState(invoice.issued_on?.slice(0, 10) || today());
  const [documentNumber, setDocumentNumber] = useState(invoice.document_number ?? '');

  const [drafts, setDrafts] = useState<PurchaseLineDraft[]>([]);
  const [inicializado, setInicializado] = useState(false);
  // Al cargar el catálogo, cada línea arranca con su mejor propuesta: la que
  // coincide por nombre usa ese producto; la que no, arranca como nueva —
  // mismo criterio de coincidencia (nombre, recortado y en minúscula) que ya
  // usaba el paso «agregar productos» que esto reemplaza. Sin descripción o
  // sin cantidad no hay con qué armar una línea de compra: arranca destildada
  // para que sea un gesto de más, no de menos, incluirla.
  if (!inicializado && inventario.data) {
    const porNombre = new Map<string, CatalogItem>(
      items.map((i): [string, CatalogItem] => [i.name.trim().toLowerCase(), i]));
    setDrafts(invoice.lines.map((l, i) => {
      const coincide = l.detalle ? porNombre.get(l.detalle.trim().toLowerCase()) ?? null : null;
      return {
        key: i,
        detalle: l.detalle, unidad: l.unidad,
        cantidadFactura: l.cantidad, precioFactura: l.precio_unitario,
        included: Boolean(l.detalle?.trim()) && Boolean(l.cantidad),
        isNew: !coincide,
        itemId: coincide?.id ?? '',
        newName: l.detalle?.trim() ?? '',
        quantity: cantidadDe(l.cantidad),
        unitPrice: precioDe(l.precio_unitario),
      };
    }));
    setInicializado(true);
  }

  const incluidas = drafts.filter((d) => d.included);
  const valid = Boolean(purchasedOn) && incluidas.length > 0
    && incluidas.every((d) => Number(d.quantity) > 0 && (d.isNew ? d.newName.trim() : d.itemId));

  function patch(key: number, cambios: Partial<PurchaseLineDraft>) {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...cambios } : d)));
  }

  /**
   * Los productos nuevos se crean primero, uno por uno — cada línea de compra
   * necesita un `inventory_item_id` real — y recién con todos los id
   * resueltos se llama a `POST /purchases` **una sola vez**: esa es la que
   * mueve el stock y asienta el gasto en un solo batch atómico (§78, ver
   * `finance.ts`). Si un producto se crea y la compra después falla, el
   * producto queda —no hay nada que deshacer, es el mismo riesgo que ya
   * asume `ItemModal` con el alta rápida de proveedor.
   */
  const mutation = useApiMutation(
    async () => {
      const lineasCompra: { inventory_item_id: string; quantity: string; unit_price: number; tax: number }[] = [];
      for (const d of incluidas) {
        const itemId = d.isNew
          ? (await api.post<{ id: string }>('/admin/inventory/items', {
            name: d.newName.trim(),
            purchase_unit: d.unidad?.trim() || 'unidad',
            preferred_supplier_id: supplierId,
          })).id
          : d.itemId;
        lineasCompra.push({
          inventory_item_id: itemId,
          quantity: d.quantity,
          unit_price: d.unitPrice ? (parseMoneyInput(d.unitPrice) ?? 0) : 0,
          tax: 0, // No hay impuesto por línea en ninguna de las dos fuentes; inventarlo sería adivinar.
        });
      }

      await api.post('/admin/purchases', {
        supplier_id: supplierId,
        purchased_on: purchasedOn,
        document_number: documentNumber.trim() || undefined,
        items: lineasCompra,
      });
    },
    ['/admin/inventory', '/admin/transactions', '/admin/suppliers'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={`Registrar compra · ${supplierName}`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Registrando…' : 'Registrar compra'}
          </Button>
        </>
      }
    >
      {/* `mutation.error.message` ya trae, para `purchase_already_registered`,
          el número de factura y la fecha de la compra que chocó (ver
          `finance.ts POST /purchases`) — no hace falta traducir el código. */}
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      {source === 'pdf' && (
        <Note tone="warn">
          Cantidad y precio salen de una lectura de mejor esfuerzo del PDF: revisalos antes de
          confirmar, sobre todo si algún número se ve raro.
        </Note>
      )}

      <div className="grid grid--2">
        <Field label="Fecha de la compra" required>
          <input className="input" type="date" value={purchasedOn} onChange={(e) => setPurchasedOn(e.target.value)} />
        </Field>
        <Field label="N.º de factura" hint="Con el número, el sistema rechaza la misma factura cargada dos veces.">
          <input className="input" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} />
        </Field>
      </div>

      <Field
        label="Productos de la factura"
        required
        hint="Por línea: a qué producto corresponde (o si es nuevo), cuánto y a qué precio unitario. Lo que venga sin marcar no entra a la compra."
      >
        <Async query={inventario}>
          {() => (drafts.length === 0 ? (
            <Note>Esta factura no trajo líneas de detalle legibles.</Note>
          ) : (
            <div className="mt-1" style={{ display: 'grid', gap: 8 }}>
              {drafts.map((d) => (
                <div
                  key={d.key}
                  style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 10 }}
                >
                  <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={d.included}
                      onChange={() => patch(d.key, { included: !d.included })}
                    />
                    <span>
                      {d.detalle || <span className="muted">Sin descripción legible en esta línea</span>}
                      <span className="small muted">
                        {' '}· factura: {d.cantidadFactura ?? '—'} {d.unidad ?? ''}
                        {d.precioFactura && ` · ₡${precioDe(d.precioFactura)} c/u`}
                      </span>
                    </span>
                  </label>

                  {d.included && (
                    <div className="mt-2" style={{ paddingLeft: 26, display: 'grid', gap: 6 }}>
                      <label className="row" style={{ gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={d.isNew}
                          onChange={() => patch(d.key, { isNew: !d.isNew })}
                        />
                        <span className="small">Es un producto nuevo</span>
                      </label>

                      {d.isNew ? (
                        <input
                          className="input"
                          placeholder="Nombre del producto"
                          value={d.newName}
                          onChange={(e) => patch(d.key, { newName: e.target.value })}
                        />
                      ) : (
                        <select
                          className="select"
                          value={d.itemId}
                          onChange={(e) => patch(d.key, { itemId: e.target.value })}
                        >
                          <option value="">Elegí un producto…</option>
                          {items.map((it) => (
                            <option key={it.id} value={it.id}>{it.name} ({it.purchase_unit})</option>
                          ))}
                        </select>
                      )}

                      <div className="row row--wrap">
                        <input
                          className="input" type="number" min="0.01" step="0.01" style={{ maxWidth: 110 }}
                          placeholder="Cantidad"
                          value={d.quantity}
                          onChange={(e) => patch(d.key, { quantity: e.target.value })}
                        />
                        <input
                          className="input input--money" type="text" inputMode="decimal" style={{ maxWidth: 140 }}
                          placeholder="₡ por unidad"
                          value={d.unitPrice}
                          onChange={(e) => patch(d.key, { unitPrice: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </Async>
      </Field>
    </Modal>
  );
}
